import { v4 as uuidv4 } from 'uuid';
import useConfig from '@/composables/useConfig';
import useMqtt from '@/composables/useMqtt';
import useSequence from '@/composables/useSequence';
import { SYSTEM_VARIABLES_MAP, VARIABLE_VALUE_TYPES } from '@/js/constants';

const commandTypes = {
  CONNECTION_COMMAND_REQUEST: 'CONNECTION_COMMAND_REQUEST',
  CONNECTION_RESPONSE: 'CONNECTION_RESPONSE',
  COMMAND_TO_EXECUTE: 'COMMAND_TO_EXECUTE',
  CONNECTION_DECLINE: 'CONNECTION_DECLINE',
};

const TARGET_MODES = {
  RANDOM: 'random',
  ALL: 'all',
};

const { currentConfig, getVariable, setVariables, setFlowStateBackToStart } = useConfig();

let basePublicTopic;
let frameTopic;
const communicationTopics = [];
let params;
let resolveCallback;
const COMMUNICATION_TIMEOUT = 5000; // 5 sec
const TARGET_MODE_TIMEOUT = 500; // 500ms
const SSRF_EXECUTION_TIMEOUT = 60000; // 60 sec

/*
A & B robots -> listenRobotCommand()
A -> executeRobotCommand()
B -> connectionRequestHandler()
A -> connectionResponseHandler()
B -> commandExecuteHandler()
* */

export default function useRobotCommand() {
  let noAvailableTimeout;
  let targetModeAllTimeout;
  let skipNextResponse = null;
  async function commandExecuteHandler(err, { topic, message }) {
    const { targetElement, variables } = JSON.parse(message);
    if (variables?.length) {
      setVariables(variables);
    }
    const { mqttUnsubscribe } = useMqtt();
    await mqttUnsubscribe({ scope: 'public', topic }, commandExecuteHandler);
    const { interruptSequence, runSequence } = useSequence();
    await interruptSequence();
    setFlowStateBackToStart(targetElement);
    await runSequence(targetElement);
  }

  async function stopRobotCommandCommunication() {
    clearTimeout(noAvailableTimeout);
    const { mqttUnsubscribe } = useMqtt();
    await Promise.all(
      communicationTopics.map(({ topic, handler }) => mqttUnsubscribe({
        scope: 'public',
        topic,
      }, handler)),
    );
  }

  async function connectionRequestHandler(err, { message }) {
    const { type, sourceUuid } = JSON.parse(message);
    if (type === commandTypes.CONNECTION_COMMAND_REQUEST) {
      const targetUuid = uuidv4();
      const { mqttPublish, mqttSubscribe, mqttUnsubscribe } = useMqtt();
      const targetTopic = `${basePublicTopic}/${targetUuid}`;
      await Promise.all([
        mqttSubscribe(
          { scope: 'public', topic: targetTopic },
          commandExecuteHandler,
        ),
        mqttPublish(
          { scope: 'public', topic: `${basePublicTopic}/${sourceUuid}` },
          JSON.stringify({
            type: commandTypes.CONNECTION_RESPONSE,
            targetUuid,
          }),
        ),
      ]);
      communicationTopics.push({
        topic: targetTopic,
        handler: commandExecuteHandler,
      });

      setTimeout(async () => {
        await mqttUnsubscribe({ scope: 'public', topic: targetTopic }, commandExecuteHandler);
      }, TARGET_MODE_TIMEOUT);
    }
  }

  async function listenRobotCommand() {
    const { externalCommands } = currentConfig.value;
    if (externalCommands.robotCommand) {
      basePublicTopic = `public/${currentConfig.value.projectId}`;
      frameTopic = `${basePublicTopic}/${currentConfig.value.frameId}`;
      const { mqttSubscribe } = useMqtt();
      await mqttSubscribe({ scope: 'public', topic: frameTopic }, connectionRequestHandler);
    }
  }

  async function noRobotAvailable() {
    await stopRobotCommandCommunication();
    if (params.unavailableNext) {
      const { runSequence, interruptSequence } = useSequence();
      await interruptSequence();
      setFlowStateBackToStart(params.unavailableNext);
      await runSequence(params.unavailableNext);
    }
  }

  async function connectionResponseHandler(err, { topic, message }) {
    const { targetUuid, type } = JSON.parse(message);
    if (err || type === commandTypes.CONNECTION_DECLINE || skipNextResponse) {
      return;
    }
    skipNextResponse = params.targetRobotMode === TARGET_MODES.RANDOM;
    clearTimeout(noAvailableTimeout);
    clearTimeout(targetModeAllTimeout);

    const { mqttPublish, mqttUnsubscribe } = useMqtt();
    await mqttPublish(
      { scope: 'public', topic: `${basePublicTopic}/${targetUuid}` },
      JSON.stringify({
        type: commandTypes.COMMAND_TO_EXECUTE,
        targetElement: params.target.element,
        variables: params.targetRobotType === 'PLURAL_ONLINE'
          ? params?.variables?.incoming?.reduce((res, id) => {
            const v = getVariable(id);
            if (v.value !== undefined) {
              res.push({
                id,
                name: v.name,
                value: {
                  type: VARIABLE_VALUE_TYPES.PRIMITIVE,
                  value: typeof v.value === 'function' ? v.value() : v.value,
                },
              });
            }
            return res;
          }, [])
          : undefined,
      }),
    );

    if (skipNextResponse) {
      await mqttUnsubscribe(
        { scope: 'public', topic },
        connectionResponseHandler,
      );
      await resolveCallback();
    } else {
      // if no message for a certain threshold, then unsubscribe and resolve
      targetModeAllTimeout = setTimeout(async () => {
        await mqttUnsubscribe(
          { scope: 'public', topic },
          connectionResponseHandler,
        );
        await resolveCallback();
      }, TARGET_MODE_TIMEOUT);
    }
  }

  async function ssrfResponseHandler(err, { topic, message }) {
    const { error, variables } = JSON.parse(message);
    if (err || error) {
      return;
    }
    clearTimeout(noAvailableTimeout);
    const { mqttUnsubscribe } = useMqtt();
    await mqttUnsubscribe(
      { scope: 'public', topic },
      ssrfResponseHandler,
    );

    if (variables?.length) {
      setVariables(variables);
    }

    await resolveCallback();
  }

  async function executeSSRFRobotCommand() {
    const { mqttSubscribe, mqttPublish } = useMqtt();
    const sourceUuid = uuidv4();
    const shouldWaitForResponse = !!params.variables?.outgoing?.length;
    const promises = [
      shouldWaitForResponse
        ? mqttSubscribe(
          { scope: 'public', topic: `public/${sourceUuid}` },
          ssrfResponseHandler,
        )
        : Promise.resolve(),
      mqttPublish(
        {
          scope: 'public',
          topic: `public/ssrf/robot-command/${params.target.robotId}/${params.target.element}`, // we need target robot id
        },
        JSON.stringify({
          sourceUuid,
          variables: {
            incoming: params.variables.incoming.reduce((res, id) => {
              const v = getVariable(id);
              if (v.value !== undefined) {
                res.push({
                  id,
                  value: {
                    type: VARIABLE_VALUE_TYPES.PRIMITIVE,
                    value: typeof v.value === 'function' ? v.value() : v.value,
                  },
                });
              }
              return res;
            }, []),
            outgoing: params.variables.outgoing,
          },
          language: getVariable(SYSTEM_VARIABLES_MAP.LANGUAGE_ISO2.id).value,
        }),
      ),
    ];

    await Promise.all(promises);

    if (shouldWaitForResponse) {
      communicationTopics.push({
        topic: `public/${sourceUuid}`,
        handler: ssrfResponseHandler,
      });

      noAvailableTimeout = setTimeout(async () => {
        await noRobotAvailable();
      }, SSRF_EXECUTION_TIMEOUT);
    } else {
      await resolveCallback();
    }
  }

  async function executeRobotCommand(parameters, onFinish) {
    params = parameters;
    resolveCallback = onFinish;
    const { mqttSubscribe, mqttPublish } = useMqtt();
    const sourceUuid = uuidv4();
    if (params.targetRobotType === 'SSRF') {
      await executeSSRFRobotCommand();
    } else {
      await Promise.all([
        mqttSubscribe(
          { scope: 'public', topic: `${basePublicTopic}/${sourceUuid}` },
          connectionResponseHandler,
        ),
        mqttPublish(
          { scope: 'public', topic: `${basePublicTopic}/${params.target.frame}` },
          JSON.stringify({
            type: commandTypes.CONNECTION_COMMAND_REQUEST,
            sourceUuid,
          }),
        ),
      ]);

      communicationTopics.push({
        topic: `${basePublicTopic}/${sourceUuid}`,
        handler: connectionResponseHandler,
      });

      noAvailableTimeout = setTimeout(async () => {
        await noRobotAvailable();
      }, COMMUNICATION_TIMEOUT);
    }
  }

  return {
    listenRobotCommand,
    executeRobotCommand,
    stopRobotCommandCommunication,
  };
}
